import {useCallback, useContext, useMemo, useRef} from 'react';

import {
  INITIAL_BILLING_STATE,
  type WBAgentBillingReadyState,
  type WBAgentBillingState,
} from '../../../agentChat/billing';
import {envIsPublicCloud} from '../../../config';
import {useGetWbagentUsageDetails} from '../../../util/usage/wbagent';
import {AccountSelectorContext} from '../../Search/SearchNav/AccountSelectorContextProvider';
import {AccountPlanType} from '../../Search/SearchNav/types';

/**
 * useComputeWBAgentBilling derives the WB Agent billing snapshot from the
 * selected account, reusing useGetWbagentUsageDetails. When the billing
 * feature is off for the org, the snapshot is `unenforced` (accessible).
 *
 * NOTE: This function is for use by the agent chat provider component; external
 * components should read the store via useWBAgentBilling/useWBAgentBillingAccess.
 */
export function useComputeWBAgentBilling(enabled: boolean): {
  billing: WBAgentBillingState;
  refresh: () => Promise<void>;
} {
  const {selectedAccount} = useContext(AccountSelectorContext);
  // useGetWbagentUsageDetails is multi-tenant (SaaS) only; off public cloud,
  // pass no account so it skips its billing queries. Dedicated needs a
  // separate billing path.
  const account = enabled && envIsPublicCloud ? selectedAccount : null;
  const {
    wbagentLimits,
    usage,
    loading,
    error,
    refetch,
    isUsagePaid,
    billingPeriodStart,
    billingPeriodEnd,
    isFeatureEnabled,
  } = useGetWbagentUsageDetails(account);
  const {enforceWbagentFreeLimit, wbagentFreeLimit, wbagentSafetyLimit} =
    wbagentLimits;

  // The last real, network-derived ready snapshot, kept so a transient refresh
  // error doesn't lock out an account billing already cleared. Keyed by account
  // so a switch never serves another account's billing.
  const accountId = selectedAccount?.id ?? null;
  const lastReady = useRef<{
    accountId: string;
    state: WBAgentBillingReadyState;
  } | null>(null);

  const billingPeriodStartAt = billingPeriodStart?.getTime() ?? null;
  const billingPeriodEndAt = billingPeriodEnd?.getTime() ?? null;

  const billing = useMemo<WBAgentBillingState>(() => {
    if (!enabled || selectedAccount == null) {
      return INITIAL_BILLING_STATE;
    }
    if (!isFeatureEnabled) {
      return {status: 'unenforced'};
    }
    if (loading) {
      return INITIAL_BILLING_STATE;
    }
    if (error != null) {
      // Fail closed only before the first success; afterward keep serving the
      // last good snapshot so a flaky refresh can't lock out an entitled user.
      return lastReady.current?.accountId === accountId
        ? lastReady.current.state
        : {status: 'error'};
    }

    // Trials are Enterprise-only; everyone else maps to paygo or free quota.
    const isTrial =
      selectedAccount.accountPlanType === AccountPlanType.Enterprise &&
      !isUsagePaid;
    // Expiry keys off the billing-period end, not a subscription date.
    const isTrialExpired =
      billingPeriodEndAt != null && billingPeriodEndAt <= Date.now();

    const ready: WBAgentBillingReadyState = {
      billingPeriodEndAt,
      billingPeriodStartAt,
      isPaygoEnabled: isUsagePaid,
      isQuotaEnforced: enforceWbagentFreeLimit,
      isTrial,
      isTrialExpired,
      quotaLimit: wbagentFreeLimit,
      quotaSafetyLimit: wbagentSafetyLimit,
      quotaUsed: usage,
      status: 'ready',
    };
    // Record the success so a later error can fall back to it. `accountId` is
    // non-null here since a ready state always has a selected account.
    if (accountId != null) {
      lastReady.current = {accountId, state: ready};
    }
    return ready;
  }, [
    accountId,
    billingPeriodEndAt,
    billingPeriodStartAt,
    enabled,
    enforceWbagentFreeLimit,
    error,
    isFeatureEnabled,
    isUsagePaid,
    loading,
    selectedAccount,
    usage,
    wbagentFreeLimit,
    wbagentSafetyLimit,
  ]);

  // Drop overlapping refreshes; Apollo refetch forces a network request and
  // does not dedupe, so guard it. The next turn-settle or open re-syncs.
  const isRefreshing = useRef(false);
  const refresh = useCallback(async (): Promise<void> => {
    if (isRefreshing.current) {
      return;
    }

    isRefreshing.current = true;
    try {
      await refetch();
    } finally {
      isRefreshing.current = false;
    }
  }, [refetch]);

  return {billing, refresh};
}
